import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View, ActivityIndicator, Platform } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import * as Updates from 'expo-updates';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppColors } from '../../hooks/useAppColors';
import { useAuthStore } from '../../stores/authStore';
import { usePaymentVerificationStream } from '../../hooks/usePaymentVerificationStream';
import { paymentsApi } from '../../api/payments';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import type { RootStackParamList } from '../../navigation/types';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

export default function PendingVerificationBanner() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const queryClient = useQueryClient();
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const paymentId = useAuthStore((s) => s.pendingVerificationPaymentId);
  const hasPausedMembership = useAuthStore((s) => s.hasPausedMembership);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setShowActivationSuccessBanner = useAuthStore((s) => s.setShowActivationSuccessBanner);
  const [isChecking, setIsChecking] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const { connectionState, lastResult, reconnect } = usePaymentVerificationStream(paymentId);
  const activatedRef = useRef(false);

  const reloadApp = () => {
    if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      Updates.reloadAsync();
    }
  };

  useEffect(() => {
    if (!lastResult || activatedRef.current) return;

    if (lastResult.status === 'completed') {
      activatedRef.current = true;
      setPendingVerificationPaymentId(null);
      setNeedsSubscription(false);
      setShowActivationSuccessBanner(true);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
      queryClient.invalidateQueries({ queryKey: ['activeMembership'] });
      queryClient.invalidateQueries({ queryKey: ['pendingVerification'] });
      reloadApp();
    }
  }, [lastResult, setPendingVerificationPaymentId, setNeedsSubscription, setShowActivationSuccessBanner, queryClient]);

  if (!paymentId) return null;

  const handleNavigateToVietQR = async () => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const data = await paymentsApi.getPendingVerification();
      if (data && data.payment_id && data.qr_url && data.order_number) {
        navigation.navigate('VietQR', {
          payment_id: data.payment_id,
          order_number: data.order_number,
          amount: data.amount || '0',
          qr_url: data.qr_url,
          bank_bin: data.bank_bin || '',
          account_number: data.account_number || '',
          account_name: data.account_name || '',
          order_items: data.order_items || [],
          showQr: true,
        });
      }
    } catch (err) {
      console.error('Failed to get pending verification details for navigation:', err);
    } finally {
      setIsNavigating(false);
    }
  };

  const handleCheckNow = async () => {
    setIsChecking(true);
    try {
      const res = await paymentsApi.getVietQRPaymentStatus(paymentId);
      if (res.status === 'completed') {
        setPendingVerificationPaymentId(null);
        setNeedsSubscription(false);
        setShowActivationSuccessBanner(true);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
        queryClient.invalidateQueries({ queryKey: ['activeMembership'] });
        queryClient.invalidateQueries({ queryKey: ['pendingVerification'] });
        reloadApp();
      } else {
        reconnect();
      }
    } catch {
      reconnect();
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <View
      style={[
        styles.banner,
        {
          backgroundColor: colors.warning + '20',
          borderBottomColor: colors.warning,
          // Topmost element on screen: keep clear of the OS status bar / notch.
          // (When the paused banner is visible it sits above us and already
          // absorbs the inset.)
          paddingTop: 10 + (hasPausedMembership ? 0 : insets.top),
        },
      ]}
    >
      <TouchableOpacity
        style={styles.touchArea}
        activeOpacity={0.7}
        onPress={handleNavigateToVietQR}
        disabled={isNavigating}
      >
        <Icon name="clock-check-outline" size={18} color={colors.warning} />
        {isNavigating ? (
          <ActivityIndicator size="small" color={colors.warning} style={{ marginHorizontal: 4 }} />
        ) : (
          <Text style={[styles.bannerText, { color: colors.text, flex: 1 }]}>
            {t('pendingBanner.title')}
            <Text style={[styles.bannerLinkText, { color: colors.accent }]}>
              {' '}({t('pendingBanner.viewQR', 'View payment QR')})
            </Text>
          </Text>
        )}
      </TouchableOpacity>
      {connectionState === 'connected' ? (
        <View style={styles.liveBadge}>
          <ActivityIndicator size="small" color={colors.success} />
          <Text style={[styles.liveText, { color: colors.success }]}>{t('pendingBanner.listening')}</Text>
        </View>
      ) : (
        <TouchableOpacity
          onPress={handleCheckNow}
          activeOpacity={0.7}
          style={styles.checkBtn}
          disabled={isChecking}
        >
          {isChecking ? (
            <ActivityIndicator size="small" color={colors.accent} />
          ) : (
            <Text style={[styles.checkBtnText, { color: colors.accent }]}>{t('pendingBanner.checkNow')}</Text>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  touchArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bannerText: {
    fontSize: 13,
    fontWeight: '600',
  },
  bannerLinkText: {
    fontSize: 13,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  liveText: {
    fontSize: 11,
    fontWeight: '700',
  },
  checkBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  checkBtnText: {
    fontSize: 12,
    fontWeight: '700',
  },
});
